import React from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { documentService } from '@/services/api/documentService';
import { QUERY_KEYS } from '@/services/queryClient';
import { activityLabel } from '../documentUi';

dayjs.extend(relativeTime);

interface DocumentsActivityDrawerProps {
  open: boolean;
  onClose: () => void;
}

const DocumentsActivityDrawer: React.FC<DocumentsActivityDrawerProps> = ({ open, onClose }) => {
  const activityQuery = useQuery({
    queryKey: QUERY_KEYS.documentActivity,
    queryFn: () => documentService.listActivity(50),
    enabled: open,
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Activity
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={18} />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Recent folder and file changes in the library.
        </Typography>
        {activityQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (activityQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No activity yet. Upload, move, or hide a folder to start the trail.
          </Typography>
        ) : (
          <List dense disablePadding>
            {(activityQuery.data || []).map((item) => (
              <ListItem key={item.id} alignItems="flex-start" sx={{ px: 0, py: 1.25 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {activityLabel(item.action)}
                      {item.detail ? ` · ${item.detail}` : ''}
                    </Typography>
                  }
                  secondary={
                    <>
                      {item.actorName || 'Someone'}
                      {item.documentName ? ` · ${item.documentName}` : ''}
                      {item.categoryName && !item.documentName ? ` · ${item.categoryName}` : ''}
                      {' · '}
                      {dayjs(item.createdAt).fromNow()}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default DocumentsActivityDrawer;
